import {
  Show,
  Field,
  Button,
  GridItem,
  SimpleGrid,
  FormatNumber,
  useBreakpointValue,
} from "@chakra-ui/react";
import { z as zod } from "zod";
import { useMemo } from "react";
import Decimal from "decimal.js";
import { useFormik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";

import {
  NumberInputRoot,
  NumberInputField,
} from "@/components/ui/number-input";
import { CreateOrderDTOLine } from "../lib/dto";
import { useCreateOrderForm } from "../lib/create-order-context";

import { ItemSelect } from "@widgets/item-select";
import { UnitBreakdown } from "@shared/ui/rounded-badge";
import { type Item, DEFAULT_UNIT_OF_MEASURE } from "@entities/item";

const validationSchema = zod.object({
  item: zod.object({ name: zod.string() }).required(),
  quantity: zod.string().nonempty().min(1),
});

export const CreateOrderFormLineCreator: React.FC = () => {
  const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: true });
  const { values: masterFormValues, setFieldValue: setMasterFormFieldValue } =
    useCreateOrderForm();

  const excludeItems = useMemo<Item[]>(
    () => masterFormValues.lines.map((line) => line.item as Item),
    [masterFormValues.lines],
  );

  const { values, errors, isValid, setFieldValue, submitForm, resetForm } =
    useFormik<CreateOrderDTOLine>({
      initialValues: {
        id: Date.now() * -1,
        item: void 0,
        quantity: "",
      },
      validateOnBlur: true,
      validateOnMount: true,
      validationSchema: toFormikValidationSchema(validationSchema),
      onSubmit: (_values) => {
        setMasterFormFieldValue("lines", [
          ...(masterFormValues.lines ?? []),
          { ..._values, meta: { customProduct: (_values.item?.id ?? -1) < 0 } },
        ]);

        resetForm();
      },
    });

  const piecesCount = useMemo<number>(() => {
    if (!values?.item || !values.quantity || !isFinite(Number(values.quantity)))
      return 0;

    if (Number(values.quantity) < 0) return 0;

    return new Decimal(values.quantity)
      .times(values.item?.unitsInUOO)
      .toNumber();
  }, [values]);

  const quantityErrors = useMemo(() => {
    if (!values.item) return errors.quantity;

    const minQty = values.item?.minimalOrderQuantity ?? 1;

    const minOrderSatisfied = Number(values.quantity) >= minQty;

    return [
      minOrderSatisfied
        ? ""
        : `Minimal order quantity is ${minQty} for this item`,
      errors.quantity,
    ]
      .filter(Boolean)
      .join(", ");
  }, [errors.quantity, values.quantity, values.item]);

  const renderQuantityPreview = () => (
    <>
      <FormatNumber value={piecesCount} />{" "}
      {values?.item?.unitOfMeasure ?? DEFAULT_UNIT_OF_MEASURE}
    </>
  );

  return (
    <SimpleGrid w="full" columns={12} gap={4}>
      <GridItem colSpan={{ base: 6, md: 5 }}>
        <Field.Root invalid={Boolean(errors?.item)}>
          <Field.Label>
            Select product
            <Field.RequiredIndicator />
          </Field.Label>
          <ItemSelect
            value={values.item}
            exclude={excludeItems}
            onChangeItem={(i) => setFieldValue("item", i)}
            getCreateNewItemLabel={(name) => `Request "${name}"`}
          />
          <Field.ErrorText>{errors.item}</Field.ErrorText>
        </Field.Root>
      </GridItem>

      <GridItem colSpan={{ base: 6, md: 5 }}>
        <Field.Root invalid={Boolean(quantityErrors)}>
          <Field.Label>
            Qty
            <Field.RequiredIndicator />
          </Field.Label>
          <NumberInputRoot
            value={values.quantity as string}
            onValueChange={(e) => setFieldValue("quantity", e.value)}
          >
            <NumberInputField />
          </NumberInputRoot>

          <Field.HelperText>
            {piecesCount > 0 && values.item ? (
              <UnitBreakdown
                variant={quantityErrors ? "error" : "default"}
                quantity={Number(values.quantity)}
                unitOfOrder={values.item.unitOfOrder}
                unitsInUOO={values.item.unitsInUOO}
                unitOfMeasure={values.item.unitOfMeasure}
              />
            ) : (
              (values?.item?.unitOfOrder ?? "UOO")
            )}
          </Field.HelperText>
          <Field.ErrorText>{quantityErrors}</Field.ErrorText>
        </Field.Root>
      </GridItem>

      <GridItem colSpan={{ base: 12, md: 2 }}>
        <Field.Root>
          <Field.Label opacity={0}>{"."}</Field.Label>
          <Button
            onClick={submitForm}
            w="full"
            disabled={!isValid || !!quantityErrors}
          >
            Add
            <Show when={isMobile}>
              {" | "} {renderQuantityPreview()}
            </Show>
          </Button>
        </Field.Root>
      </GridItem>
    </SimpleGrid>
  );
};
